import { ThemeEvents } from '@theme/events';

/** @type {Readonly<{ maxOrdersFreeDelivery: number }>} */
const DEFAULTS = Object.freeze({
  maxOrdersFreeDelivery: 3,
});

/**
 * Cart free-delivery / free-gift meters. Liquid owns customer-facing copy
 * (`data-free-delivery-reason`: tjc_plus | first_orders | threshold | none).
 * This element mirrors the first-N-orders perk onto `data-free-delivery-by-order-count`
 * after cart updates when the node persists (checkout shipping still follows JsRates).
 *
 * @extends HTMLElement
 */
class CartMeterComponent extends HTMLElement {
  /** @type {(() => void) | null} */
  #cartUpdateBound = null;

  connectedCallback() {
    this.#applyOrderCountPerk();
    this.#cartUpdateBound = () => {
      this.#applyOrderCountPerk();
    };
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
  }

  disconnectedCallback() {
    if (this.#cartUpdateBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
      this.#cartUpdateBound = null;
    }
  }

  /**
   * Mirrors Liquid `delivery_met` for the first-N-orders perk; sets a data attribute for
   * styling or analytics. Section re-renders after cart changes refresh Liquid output;
   * this keeps the attribute in sync when the element persists.
   *
   * @returns {void}
   */
  #applyOrderCountPerk() {
    const perkEnabled = this.dataset.orderCountPerkEnabled !== 'false';
    const loggedIn = this.dataset.loggedIn === 'true';
    const orders = Number.parseInt(String(this.dataset.ordersCount ?? '0'), 10);
    const max = Number.parseInt(
      String(this.dataset.maxOrdersFreeDelivery ?? String(DEFAULTS.maxOrdersFreeDelivery)),
      10
    );
    const safeMax = Number.isFinite(max) && max > 0 ? max : DEFAULTS.maxOrdersFreeDelivery;
    const safeOrders = Number.isFinite(orders) ? orders : 0;
    const perk = perkEnabled && loggedIn && safeOrders < safeMax;
    this.dataset.freeDeliveryByOrderCount = perk ? 'true' : 'false';
  }
}

if (!customElements.get('cart-meter-component')) {
  customElements.define('cart-meter-component', CartMeterComponent);
}
